import React from 'react';

/**
 * Shape-matched loading placeholders for a block that is still fetching.
 *
 * Deliberately per-type rather than one generic shimmer: the point of a
 * skeleton is that the card does not change shape when the data lands, so a
 * table's placeholder is rows, a chart's is bars, and a scorecard's is one
 * big number. A single grey rectangle everywhere would still reflow.
 *
 * Presentation only — `aria-hidden`. The status a screen reader needs comes
 * from the surrounding container's aria-busy and, on a running block, from
 * the RunTimeline text, which stays visible above these.
 */

const bar = (width, height = 11, extra = {}) => ({
    width, height, borderRadius: '4px', ...extra,
});

const s = {
    wrap: { display: 'flex', flexDirection: 'column', gap: '10px', padding: '2px 0', width: '100%' },
    lines: { display: 'flex', flexDirection: 'column', gap: '8px' },
    row: { display: 'flex', gap: '8px' },
    chart: { display: 'flex', alignItems: 'flex-end', gap: '8px', height: '110px', paddingTop: '6px' },
    baseline: { height: '1px', background: '#e3e6ec', marginTop: '6px' },
    imageBox: { width: '100%', flex: 1, minHeight: '120px', borderRadius: '6px' },
};

// Fixed, uneven heights rather than random ones: a skeleton that reshuffles on
// every render reads as broken, and Math.random would do exactly that.
const BAR_HEIGHTS = ['62%', '88%', '45%', '74%', '96%', '58%', '80%'];
const ROW_WIDTHS = ['92%', '78%', '85%', '70%', '88%'];

const Line = ({ w, h }) => <div className="cvx-skel" style={bar(w, h)} />;

function TableSkeleton({ rows = 5 }) {
    return (
        <div style={s.lines}>
            <div style={s.row}>
                {['26%', '22%', '24%', '18%'].map((w, i) => <Line key={i} w={w} h={9} />)}
            </div>
            <div style={s.baseline} />
            {ROW_WIDTHS.slice(0, rows).map((w, r) => (
                <div key={r} style={s.row}>
                    {['26%', '22%', '24%', '18%'].map((cw, c) => (
                        <Line key={c} w={c === 0 ? cw : `calc(${cw} - 2%)`} h={11} />
                    ))}
                </div>
            ))}
        </div>
    );
}

function ChartSkeleton() {
    return (
        <div>
            <div style={s.chart}>
                {BAR_HEIGHTS.map((h, i) => (
                    <div key={i} className="cvx-skel" style={{ flex: 1, height: h, borderRadius: '3px 3px 0 0' }} />
                ))}
            </div>
            <div style={s.baseline} />
        </div>
    );
}

export default function BlockSkeleton({ type }) {
    if (type === 'scorecard') {
        return (
            <div style={{ ...s.wrap, gap: '12px' }} aria-hidden="true">
                <div className="cvx-skel" style={bar('58%', 30)} />
                <div className="cvx-skel" style={bar('34%', 9)} />
            </div>
        );
    }

    if (type === 'chart') {
        return <div style={s.wrap} aria-hidden="true"><ChartSkeleton /></div>;
    }

    if (type === 'table' || type === 'pivot') {
        // A pivot lands as a table too — same placeholder, so the card doesn't
        // jump when the matrix arrives.
        return <div style={s.wrap} aria-hidden="true"><TableSkeleton /></div>;
    }

    if (type === 'image') {
        return (
            <div style={{ ...s.wrap, flex: 1 }} aria-hidden="true">
                <div className="cvx-skel" style={s.imageBox} />
            </div>
        );
    }

    // agent: a headline, a few callout lines, then its viz — the shape an
    // agent block almost always lands in.
    return (
        <div style={s.wrap} aria-hidden="true">
            <div className="cvx-skel" style={bar('64%', 14)} />
            <div style={s.lines}>
                <Line w="100%" h={10} />
                <Line w="94%" h={10} />
                <Line w="72%" h={10} />
            </div>
            <div style={{ marginTop: '4px' }}><TableSkeleton rows={3} /></div>
        </div>
    );
}
